import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import { nacmeLogo } from './assets/images';
import { ResponsiveTable, MobileStudentMatchCard, MobileSidebar } from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

// Component for collapsible requirement dropdowns
const RequirementDropdown = ({ label, items, orgIndex, fieldName, expandedItems, setExpandedItems }) => {
  const dropdownKey = `${orgIndex}-${fieldName}`;
  const isExpanded = expandedItems.has(dropdownKey);
  
  const toggleDropdown = (e) => {
    e.stopPropagation(); // Prevent triggering the org expansion
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(dropdownKey)) {
      newExpanded.delete(dropdownKey);
    } else {
      newExpanded.add(dropdownKey);
    }
    setExpandedItems(newExpanded);
  };

  if (!items || items.length === 0) {
    return (
      <div>
        <p className="font-medium text-gray-700 dark:text-gray-300">{label}:</p>
        <p className="text-gray-600 dark:text-gray-400">Not specified</p>
      </div>
    );
  }

  const displayText = items.length === 1 ? items[0] : `${items.length} items`;
  const shouldShowDropdown = items.length > 1;

  return (
    <div>
      <p className="font-medium text-gray-700 dark:text-gray-300">{label}:</p>
      {shouldShowDropdown ? (
        <div>
          <button
            onClick={toggleDropdown}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            <span>{displayText}</span>
            <svg 
              className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isExpanded && (
            <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-600 rounded border text-xs">
              <ul className="list-disc list-inside space-y-1">
                {items.map((item, idx) => (
                  <li key={idx} className="text-gray-700 dark:text-gray-300">{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-600 dark:text-gray-400">{displayText}</p>
      )}
    </div>
  );
};

const AdminOrganizationMatches = () => {
  const navigate = useNavigate();
  const [organizationMatches, setOrganizationMatches] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedOrgs, setExpandedOrgs] = useState(new Set());
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  // CSV Export functionality
  const exportToCSV = (orgData) => {
    const { recruiter_info, matches } = orgData;
    
    if (matches.length === 0) {
      alert('No matches to export for this organization.');
      return;
    }

    // CSV headers
    const headers = [
      'Student ID',
      'Name', 
      'Email',
      'University',
      'GPA',
      'Major',
      'Class Year'
    ];

    // Convert matches to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...matches.map(student => [
        student.id,
        `"${student.name}"`, // Wrap in quotes to handle commas in names
        student.email,
        `"${student.university}"`, // Wrap in quotes to handle commas in university names
        student.gpa,
        `"${student.major}"`, // Wrap in quotes to handle commas in majors
        `"${student.class_year}"` // Wrap in quotes to handle commas in class years
      ].join(','))
    ];

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with organization name and timestamp
      const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD format
      const orgName = recruiter_info.organization.replace(/[^a-zA-Z0-9]/g, '_'); // Replace special chars with underscores
      link.setAttribute('download', `${orgName}_Student_Matches_${timestamp}.csv`);
      
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Export all organizations with matches
  const exportAllToCSV = () => {
    const orgsWithMatches = organizationMatches.filter(org => org.match_count > 0);
    
    if (orgsWithMatches.length === 0) {
      alert('No organizations have matches to export.');
      return;
    }

    // CSV headers with organization column
    const headers = [
      'Organization',
      'Student ID',
      'Name', 
      'Email',
      'University',
      'GPA',
      'Major',
      'Class Year'
    ];

    // Convert all matches to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...orgsWithMatches.flatMap(orgData => 
        orgData.matches.map(student => [
          `"${orgData.recruiter_info.organization}"`,
          student.id,
          `"${student.name}"`,
          student.email,
          `"${student.university}"`,
          student.gpa,
          `"${student.major}"`,
          `"${student.class_year}"`
        ].join(','))
      )
    ];

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `All_Organization_Student_Matches_${timestamp}.csv`);
      
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    fetchOrganizationMatches();
  }, []);

  const fetchOrganizationMatches = async (withDebug = false) => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setLoading(false);
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      const url = withDebug 
        ? `${BACKEND_URL}/api/organization-matches?debug=true`
        : `${BACKEND_URL}/api/organization-matches`;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setOrganizationMatches(response.data.organizations || []);
      setDebugInfo(response.data.debug || null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching organization matches:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(err.response?.data?.detail || 'Failed to fetch organization matches. Please ensure you are logged in as an admin.');
      }
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  const toggleOrganization = (index) => {
    const newExpanded = new Set(expandedOrgs);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedOrgs(newExpanded);
  };

  const expandAll = () => {
    setExpandedOrgs(new Set(organizationMatches.map((_, index) => index)));
  };

  const collapseAll = () => {
    setExpandedOrgs(new Set());
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-100 text-red-700 p-4 rounded-md">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Mobile Sidebar */}
      <MobileSidebar
        activeTab="organization-matches"
        onTabChange={(tabId) => {
          if (tabId === 'home') {
            navigate('/admin');
          } else if (tabId === 'students') {
            // Students button handled by grid menu
          } else if (tabId === 'recruiters') {
            // Recruiters button handled by grid menu
          } else if (tabId === 'profile') {
            // Logout functionality
            localStorage.removeItem('token');
            navigate('/admin');
          }
        }}
        navigate={navigate}
        isOpen={isMobileSidebarOpen}
        onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col gap-4">
            {/* Logo and Title Row */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-10 sm:h-12 w-auto drop-shadow"
              />
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                Organization Student Matches
              </h1>
            </div>
            
            {/* Action Buttons Row */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:justify-end">
              <button
                onClick={() => navigate('/admin')}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
              >
                <span className="hidden sm:inline">Back to Admin Panel</span>
                <span className="sm:hidden">Back to Admin</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {organizationMatches.length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Total Organizations
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {organizationMatches.reduce((sum, org) => sum + org.match_count, 0)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Total Matches
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {organizationMatches.filter(org => org.match_count > 0).length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Organizations with Matches
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="mb-6 flex flex-wrap gap-4">
          <button
            onClick={expandAll}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Collapse All
          </button>
          <button
            onClick={() => fetchOrganizationMatches()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh Data
          </button>
          <button
            onClick={exportAllToCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export All Matches
          </button>
          <button
            onClick={() => {
              setDebugMode(!debugMode);
              fetchOrganizationMatches(!debugMode);
            }}
            className={`px-4 py-2 rounded-lg transition-colors ${
              debugMode 
                ? 'bg-orange-600 text-white hover:bg-orange-700' 
                : 'bg-gray-400 text-white hover:bg-gray-500'
            }`}
          >
            {debugMode ? 'Hide Debug' : 'Show Debug'}
          </button>
        </div>

        {/* Debug Information */}
        {debugMode && debugInfo && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-3">
              Debug Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Total Recruiters: {debugInfo.total_recruiters}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Approved Recruiters: {debugInfo.approved_recruiters}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Pending Recruiters: {debugInfo.pending_recruiters || 0}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Sample Recruiters:
                </p>
              </div>
            </div>
            {debugInfo.recruiter_statuses && debugInfo.recruiter_statuses.length > 0 && (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-yellow-700 dark:text-yellow-300">
                      <th className="text-left p-1">ID</th>
                      <th className="text-left p-1">Name</th>
                      <th className="text-left p-1">Approved</th>
                      <th className="text-left p-1">Has Organization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debugInfo.recruiter_statuses.map((recruiter) => (
                      <tr key={recruiter.id} className="text-yellow-600 dark:text-yellow-400">
                        <td className="p-1">{recruiter.id}</td>
                        <td className="p-1">{recruiter.name}</td>
                        <td className="p-1">{recruiter.approved ? '✅' : '❌'}</td>
                        <td className="p-1">{recruiter.has_org ? '✅' : '❌'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Organization List */}
        <div className="space-y-6">
          {organizationMatches.map((orgData, index) => {
            const isExpanded = expandedOrgs.has(index);
            const { recruiter_info, matches, match_count } = orgData;
            
            return (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                {/* Organization Header */}
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => toggleOrganization(index)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                          {recruiter_info.organization}
                        </h2>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          match_count > 0 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {match_count > 0 ? `${match_count} ${match_count === 1 ? 'Match' : 'Matches'}` : 'NO MATCHES YET'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          recruiter_info.approved
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                          {recruiter_info.approved ? 'APPROVED' : 'PENDING APPROVAL'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                        <div>
                          <p><strong>Recruiter:</strong> {recruiter_info.recruiter_name}</p>
                          <p><strong>Email:</strong> {recruiter_info.recruiter_email}</p>
                        </div>
                        <div>
                          <p><strong>Company Locations:</strong> {recruiter_info.company_locations}</p>
                          <p><strong>Minimum GPA:</strong> {recruiter_info.minimum_gpa}</p>
                          
                          <div className="mt-3 space-y-2">
                            <RequirementDropdown 
                              label="Eligible Majors"
                              items={recruiter_info.eligible_majors}
                              orgIndex={index}
                              fieldName="majors"
                              expandedItems={expandedItems}
                              setExpandedItems={setExpandedItems}
                            />
                            <RequirementDropdown 
                              label="Preferred Institutions"
                              items={recruiter_info.preferred_institutions}
                              orgIndex={index}
                              fieldName="institutions"
                              expandedItems={expandedItems}
                              setExpandedItems={setExpandedItems}
                            />
                            <div>
                              <p className="font-medium text-gray-700 dark:text-gray-300">Preferred Class Years:</p>
                              <p className="text-gray-600 dark:text-gray-400">
                                {recruiter_info.preferred_class_years ? recruiter_info.preferred_class_years.join(', ') : 'Not specified'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      <svg 
                        className={`w-6 h-6 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    {/* Student Matches */}
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Student Matches ({match_count})
                        </h3>
                        {match_count > 0 && (
                          <button
                            onClick={() => exportToCSV(orgData)}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Export CSV
                          </button>
                        )}
                      </div>
                      
                      {match_count === 0 ? (
                        <div className="text-center py-8">
                          <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">📭</div>
                          <p className="text-gray-600 dark:text-gray-400 text-lg font-medium">
                            NO MATCHES YET
                          </p>
                          <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                            No students currently match all the requirements for this organization.
                          </p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Student ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  University
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  GPA
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Major
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Class Year
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                              {matches.map((student) => (
                                <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                    {student.id}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                    {student.name}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                    {student.email}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                    {student.university}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 dark:text-green-400">
                                    {student.gpa}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                    {student.major}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                    {student.class_year}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {organizationMatches.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 text-4xl sm:text-6xl mb-4">🏢</div>
            <p className="text-gray-600 dark:text-gray-400 text-base sm:text-lg font-medium">
              No organizations found
            </p>
            <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
              No approved recruiters with complete profile information.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrganizationMatches;